import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { createPortal } from "react-dom";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRemindLater?: () => Promise<void>;
  onSubmit?: (rating: number, comment: string) => Promise<boolean>;
}

const labels = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onRemindLater, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const display = hoverRating || rating;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    setLoading(true);
    try {
      // Request payload:
      // POST /feedback/feedback
      // { star_rating: number (1–5), description: string }
      const success = onSubmit ? await onSubmit(rating, comment) : true;
      if (success) {
        setSubmitted(true);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setComment("");
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div style={{ background: "#020B3F" }} className="p-5 text-white">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold">Share your feedback</h2>
            <button
              onClick={handleClose}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-white/60 text-xs">
            Help us improve by rating your experience and sharing your thoughts.
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {!submitted ? (
            <>
              {/* Stars */}
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 mb-3">
                  How would you rate your experience?
                </p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="transition-transform hover:scale-110 active:scale-95"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="34"
                        height="34"
                        fill={star <= display ? "#534AB7" : "#E5E7EB"}
                        stroke={star <= display ? "#534AB7" : "#E5E7EB"}
                        strokeWidth="1.5"
                      >
                        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                      </svg>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#534AB7] mt-2 min-h-[16px] font-medium">
                  {display > 0 ? labels[display] : ""}
                </p>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  What can we improve on?
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us how we can make the app better..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#534AB7]/20 focus:border-[#534AB7] transition resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onRemindLater ?? handleClose}
                  disabled={loading}
                  className="flex-1 border border-gray-300 text-gray-600 font-medium py-3 px-4 rounded-xl text-sm transition-all hover:bg-gray-50 active:scale-95 disabled:opacity-60"
                >
                  Remind me later
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ background: "#020B3F" }}
                  className="flex-1 text-white font-medium py-3 px-4 rounded-xl text-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </>
          ) : (
            /* Success state */
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-[#EAF3DE] flex items-center justify-center mx-auto mb-3">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#3B6D11" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-800 mb-1">Thank you for your feedback!</p>
              <p className="text-xs text-gray-500">We'll use it to improve your experience.</p>
              <button
                onClick={handleClose}
                className="mt-4 text-xs text-[#534AB7] hover:underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackModal;